import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore.js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter';

// --- Helper Functions (Statis) ---
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('id-ID').format(value);
};

const formatMonth = (monthKey) => {
  if (!monthKey) return '-';
  const [y, m] = monthKey.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${months[parseInt(m) - 1]} ${y}`;
};

function CashFlow() {
  const { user } = useAuthStore();
  const [cashFlowData, setCashFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDay, setStartDay] = useState('1');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [coopProfile, setCoopProfile] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [logoBase64, setLogoBase64] = useState('');

  const { trustees } = useTrusteesFooter(startDate, endDate);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo()
  }, []);

  // 1. Inisialisasi Periode & Info Kopi
  useEffect(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    setStartYear(String(y));
    setStartMonth('01');
    setStartDay('01');
    setEndYear(String(y));
    setEndMonth(m);
    setEndDay(d);

    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info');
        setCoopProfile(response.data?.data?.coopProfile);
        setBranchInfo(response.data?.data?.branchInfo);
      } catch (error) {
        console.error('Error fetching profile info:', error);
      }
    };
    fetchProfileInfo();
  }, []);

  // Compose date strings from dropdown parts
  useEffect(() => {
    if (startYear && startMonth && startDay) {
      setStartDate(`${startYear}-${startMonth}-${startDay}`);
    }
  }, [startYear, startMonth, startDay]);

  useEffect(() => {
    if (endYear && endMonth && endDay) {
      setEndDate(`${endYear}-${endMonth}-${endDay}`);
    }
  }, [endYear, endMonth, endDay]);

  // 2. Fetch Data Arus Kas
  const fetchCashFlowData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) return;

    setLoading(true);
    try {
      const response = await api.get('/reports/cash-flow', { params: { startDate, endDate } });
      setCashFlowData(response.data?.data);
    } catch (error) {
      console.error('Error fetching cash flow data:', error);
      setCashFlowData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchCashFlowData();
  }, [fetchCashFlowData]);

  // 3. Kalkulasi Ringkasan Finansial
  const financialSummary = useMemo(() => {
    if (!cashFlowData) return { beginning: 0, net: 0, ending: 0 };
    const beginning = cashFlowData.Totals?.SaldoAwal || 0;
    const net = cashFlowData.Totals?.NetCashFlow || 0;
    return {
      beginning,
      net,
      ending: beginning + net
    };
  }, [cashFlowData]);

  // --- 4. Logic Ekspor & Cetak ---

  const handlePrint = () => {
    if (!cashFlowData) return;
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>Cetak Arus Kas</title>
          <style>
            body { font-family: sans-serif; padding: 30px; font-size: 11px; }
            .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .kop-logo { width: 70px; height: auto; }
            .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
            .kop-badan-hukum { font-size: 12px; margin: 3px 0; }
            .kop-alamat { font-size: 11px; margin: 2px 0; }
            .kop-kontak { font-size: 10px; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #ccc; padding: 6px; }
            .text-right { text-align: right; }
            .bg-gray { background: #f2f2f2; font-weight: bold; }
            .section-header { background: #e8f4f8; font-weight: bold; padding: 5px; border: 1px solid #ccc; }
          </style>
        </head>
        <body>
          <div class="kop-surat">
              <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
              <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
              <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
              <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
              <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
            </div>
          <p><b>SALDO AWAL: ${formatCurrency(financialSummary.beginning)}</b></p>
          ${renderMonthlyHTML(cashFlowData.MonthlyBreakdown)}
          ${renderTableHTML('AKTIVITAS OPERASI', cashFlowData.AktivitasOperasi, cashFlowData.Totals?.Operasi)}
          ${renderTableHTML('AKTIVITAS INVESTASI', cashFlowData.AktivitasInvestasi, cashFlowData.Totals?.Investasi)}
          ${renderTableHTML('AKTIVITAS PENDANAAN', cashFlowData.AktivitasPendanaan, cashFlowData.Totals?.Pendanaan)}
          <table>
            <tr class="bg-gray">
              <td colspan="3">NET CHANGE KAS</td>
              <td class="text-right">${formatCurrency(financialSummary.net)}</td>
            </tr>
            <tr class="bg-gray">
              <td colspan="3">SALDO AKHIR KAS</td>
              <td class="text-right">${formatCurrency(financialSummary.ending)}</td>
            </tr>
          </table>
        ${renderTrusteesHtml(trustees)}
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const renderMonthlyHTML = (months) => {
    if (!months || months.length === 0) return '';
    return `
    <div class="section-header">RINCIAN BULANAN</div>
    <table>
      <thead><tr><th>Bulan</th><th class="text-right">Operasi</th><th class="text-right">Investasi</th><th class="text-right">Pendanaan</th><th class="text-right">Net</th></tr></thead>
      <tbody>
        ${months.map(m => `<tr><td>${formatMonth(m.bulan)}</td><td class="text-right">${formatCurrency(m.Operasi)}</td><td class="text-right">${formatCurrency(m.Investasi)}</td><td class="text-right">${formatCurrency(m.Pendanaan)}</td><td class="text-right">${formatCurrency(m.net)}</td></tr>`).join('')}
      </tbody>
    </table>
    `;
  };

  const renderTableHTML = (title, items, total) => `
    <div class="section-header">${title}</div>
    <table>
      <thead><tr><th>Tanggal</th><th>Keterangan</th><th class="text-right">Debet</th><th class="text-right">Kredit</th></tr></thead>
      <tbody>
        ${(items || []).map(i => `<tr><td>${formatDate(i.Tanggal)}</td><td>${i.Keterangan}</td><td class="text-right">${formatCurrency(i.Debet)}</td><td class="text-right">${formatCurrency(i.Kredit)}</td></tr>`).join('')}
        <tr class="bg-gray"><td colspan="3">TOTAL ${title}</td><td class="text-right">${formatCurrency(total)}</td></tr>
      </tbody>
    </table>
  `;

  const handleExportExcel = () => {
    if (!cashFlowData) return;
    const wb = XLSX.utils.book_new();
    let wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi'],
      ['LAPORAN ARUS KAS'],
      [`Periode: ${formatDate(startDate)} - ${formatDate(endDate)}`],
      [],
      ['SALDO AWAL KAS', '', '', '', financialSummary.beginning],
      [],
      ['SEKSI', 'TANGGAL', 'KETERANGAN', 'DEBET', 'KREDIT']
    ];

    const addSec = (title, items, total) => {
      wsData.push([title]);
      (items || []).forEach(i => wsData.push(['', formatDate(i.Tanggal), i.Keterangan, i.Debet, i.Kredit]));
      wsData.push(['', '', `NET ${title}`, '', total], []);
    };

    wsData.push(['RINCIAN BULANAN'], ['Bulan', 'Operasi', 'Investasi', 'Pendanaan', 'Net']);
    (cashFlowData.MonthlyBreakdown || []).forEach(m => {
      wsData.push([formatMonth(m.bulan), m.Operasi, m.Investasi, m.Pendanaan, m.net]);
    });
    wsData.push([]);
    addSec('OPERASI', cashFlowData.AktivitasOperasi, cashFlowData.Totals.Operasi);
    addSec('INVESTASI', cashFlowData.AktivitasInvestasi, cashFlowData.Totals.Investasi);
    addSec('PENDANAAN', cashFlowData.AktivitasPendanaan, cashFlowData.Totals.Pendanaan);
    wsData.push(['SALDO AKHIR KAS', '', '', '', financialSummary.ending]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'CashFlow');
    XLSX.writeFile(wb, `CashFlow_${startDate}.xlsx`);
  };

  const handleExportPDF = () => {
    if (!cashFlowData) return;
    const doc = new jsPDF({ orientation: 'l' });
    const pageWidth = doc.internal.pageSize.width;

    try {
      doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20);
    } catch (e) { console.log('Logo PDF failed:', e.message) }

    // Kop Surat Header
    doc.setFontSize(16).setFont(undefined, 'bold')
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
    doc.setFontSize(9).setFont(undefined, 'normal')
    doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
    doc.setFontSize(8).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
    doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })

    // Garis pemisah
    doc.setLineWidth(0.5)
    doc.line(10, 28, pageWidth - 10, 28)
    doc.setLineWidth(0.3)
    doc.line(10, 29, pageWidth - 10, 29)

    // Judul Laporan
    doc.setFontSize(11).setFont(undefined, 'bold')
    doc.text('LAPORAN ARUS KAS', pageWidth / 2, 35, { align: 'center' })
    doc.setFontSize(8).setFont(undefined, 'normal')
    doc.text(`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}`, pageWidth / 2, 40, { align: 'center' })

    let yPos = 45;
    doc.text(`SALDO AWAL: ${formatCurrency(financialSummary.beginning)}`, 14, yPos);

    // Monthly breakdown table
    if (cashFlowData.MonthlyBreakdown && cashFlowData.MonthlyBreakdown.length > 0) {
      autoTable(doc, {
        startY: yPos + 2,
        head: [[{ content: 'RINCIAN BULANAN', colSpan: 5 },], ['Bulan', 'Operasi', 'Investasi', 'Pendanaan', 'Net']],
        body: cashFlowData.MonthlyBreakdown.map(m => [
          formatMonth(m.bulan), formatCurrency(m.Operasi), formatCurrency(m.Investasi),
          formatCurrency(m.Pendanaan), formatCurrency(m.net)
        ]),
        theme: 'grid', styles: { fontSize: 7 }
      });
      yPos = doc.lastAutoTable.finalY + 5;
    }

    const sections = [
      { t: 'AKTIVITAS OPERASI', d: cashFlowData.AktivitasOperasi, s: cashFlowData.Totals.Operasi },
      { t: 'AKTIVITAS INVESTASI', d: cashFlowData.AktivitasInvestasi, s: cashFlowData.Totals.Investasi },
      { t: 'AKTIVITAS PENDANAAN', d: cashFlowData.AktivitasPendanaan, s: cashFlowData.Totals.Pendanaan }
    ];

    sections.forEach(sec => {
      autoTable(doc, {
        startY: yPos + 2,
        head: [[{ content: sec.t, colSpan: 4 },], ['Tanggal', 'Keterangan', 'Debet', 'Kredit']],
        body: [
          ...(sec.d || []).map(i => [formatDate(i.Tanggal), i.Keterangan, formatCurrency(i.Debet), formatCurrency(i.Kredit)]),
          [{ content: `SUBTOTAL ${sec.t}`, colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } }, { content: formatCurrency(sec.s), styles: { halign: 'right' } }]
        ],
        theme: 'grid', styles: { fontSize: 7 }
      });
      yPos = doc.lastAutoTable.finalY + 5;
    });

    doc.text(`SALDO AKHIR: ${formatCurrency(financialSummary.ending)}`, 14, yPos + 5);

    if (trustees.length > 0) {
      doc.setFontSize(6)
      const midX = pageWidth / 2
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus')
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas')
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit')
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit')

      let yL = yPos + 20
      if (pengurus.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGURUS:', 14, yL)
        yL += 3
        doc.setFont(undefined, 'normal')
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL)
          yL += 3
        })
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL)
          yL += 3
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL)
            yL += 3
          })
        }
      }

      let yR = yPos + 20
      if (pengawas.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGAWAS:', midX + 5, yR)
        yR += 3
        doc.setFont(undefined, 'normal')
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR)
          yR += 3
        })
      }
    }

    doc.save(`CashFlow_${startDate}.pdf`);
  };

  // --- 5. Render UI ---
  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0 font-weight-bold">Laporan Arus Kas</h5>
          <div className="btn-group">
            <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={loading}><i className="fas fa-file-excel mr-1"></i> Excel</button>
            <button className="btn btn-sm btn-danger" onClick={handleExportPDF} disabled={loading}><i className="fas fa-file-pdf mr-1"></i> PDF</button>
            <button className="btn btn-sm btn-light" onClick={handlePrint} disabled={loading}><i className="fas fa-print mr-1"></i> Cetak</button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded">
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL MULAI</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={startDay} onChange={(e) => setStartDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={startMonth} onChange={(e) => setStartMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL AKHIR</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={endDay} onChange={(e) => setEndDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={endMonth} onChange={(e) => setEndMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5"><div className="spinner-border text-primary"></div></div>
          ) : !cashFlowData ? (
            <div className="alert alert-info">Data tidak tersedia untuk periode ini.</div>
          ) : (
            <>
              <div className="alert alert-secondary d-flex justify-content-between">
                <span>Saldo Awal: <b>{formatCurrency(financialSummary.beginning)}</b></span>
                <span>Saldo Akhir: <b>{formatCurrency(financialSummary.ending)}</b></span>
              </div>

              <CashFlowMonthlyTable months={cashFlowData.MonthlyBreakdown} />

              <CashFlowSection title="AKTIVITAS OPERASI" items={cashFlowData.AktivitasOperasi} total={cashFlowData.Totals.Operasi} />
              <CashFlowSection title="AKTIVITAS INVESTASI" items={cashFlowData.AktivitasInvestasi} total={cashFlowData.Totals.Investasi} />
              <CashFlowSection title="AKTIVITAS PENDANAAN" items={cashFlowData.AktivitasPendanaan} total={cashFlowData.Totals.Pendanaan} />

              <div className="mt-3 p-3 bg-dark text-white rounded d-flex justify-content-between">
                <span className="font-weight-bold">SALDO AKHIR KAS PER {formatDate(endDate)}</span>
                <span className="font-weight-bold text-warning">{formatCurrency(financialSummary.ending)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CashFlowSection = ({ title, items, total }) => (
  <div className="mb-4">
    <h6 className="p-2 bg-light border-left border-primary font-weight-bold">{title}</h6>
    <table className="table table-sm table-bordered">
      <thead className="thead-light">
        <tr><th>Tanggal</th><th>Keterangan</th><th className="text-right">Debet</th><th className="text-right">Kredit</th></tr>
      </thead>
      <tbody>
        {items?.map((i, idx) => (
          <tr key={idx}><td>{formatDate(i.Tanggal)}</td><td>{i.Keterangan}</td><td className="text-right">{formatCurrency(i.Debet)}</td><td className="text-right">{formatCurrency(i.Kredit)}</td></tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="table-warning font-weight-bold">
          <td colSpan="3" className="text-right">TOTAL {title}</td>
          <td className="text-right">{formatCurrency(total)}</td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const CashFlowMonthlyTable = ({ months }) => {
  if (!months || months.length === 0) return null;
  return (
    <div className="mb-4">
      <h6 className="p-2 bg-info text-white font-weight-bold">RINCIAN ARUS KAS PER BULAN</h6>
      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead className="thead-dark">
            <tr>
              <th>Bulan</th>
              <th className="text-right">Operasi</th>
              <th className="text-right">Investasi</th>
              <th className="text-right">Pendanaan</th>
              <th className="text-right">Net Kas</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m, idx) => (
              <tr key={idx} className={m.net === 0 ? 'table-default' : 'table-default'}>
                <td><strong>{formatMonth(m.bulan)}</strong></td>
                <td className="text-right">{formatCurrency(m.Operasi)}</td>
                <td className="text-right">{formatCurrency(m.Investasi)}</td>
                <td className="text-right">{formatCurrency(m.Pendanaan)}</td>
                <td className={`text-right font-weight-bold ${m.net >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(m.net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashFlow;